import { CancelToken } from 'apisauce';
import React, { useEffect, useState } from 'react';
import PageHeader from '../../../components/PageHeader';
import { Box, Button, Stack, TablePagination, Typography, useTheme } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TablePaginationActions from '../../../components/TablePaginationActions';
import Datatable from '../../../components/DataTable';
import UserDetails from '../UserManagement/Components/UserDetails';
import TradeDetails from './Components/TradeDetails';
import SendAdvisory from './Components/SendAdvisory';
import appapi from '../../../api/apiClient';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';

import RequestLogo from '../../../assets/images/request.svg';
import AdvisoryLogo from '../../../assets/images/advisory.svg';
import { checkPermission } from '../../../redux/features/authSlice';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import AccessDenied from '../AccesDenied/AccessDenied';

const screeningStatus = {
  pending: '#BA9B1D1A',
  accepted: '#00A63226',
  rejected: '#C220201A',
  completed: '#006DFF1A',
  expired: '#14285A0F',
  pendingColor: '#E89300',
  acceptedColor: '#00A632',
  rejectedColor: '#C22020',
  completedColor: '#006DFF',
  expiredColor: '#14285A80',
};

export default function TradeManagementMain() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [selectedData, setSelectedData] = useState(null);
  const [tradeList, setTradeList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openRequestForm, setOpenRequestForm] = useState(false);

  const advisoriesPermission = useSelector((state) => checkPermission(state, 'Trade Management', 'Manage Advisories'));
  const quotePermission = useSelector((state) => checkPermission(state, 'Trade Management', 'Manage Quotes'));

  const {
    i18n: { language },
  } = useTranslation();

  const columns = [
    {
      id: 0,
      header: '#',
      accessor: 'Sno',
      width: '5%',
      default: true,
      columnEditable: false,
      activeColumn: true,
    },
    {
      id: 1,
      header: 'ID',
      accessor: 'id',
      width: '10%',
      default: true,
      columnEditable: false,
      activeColumn: true,
      filter: {
        search: false,
        filterType: 'radio',
        typeFilter: true,
        filterList: [],
      },
    },
    {
      id: 2,
      header: 'Display name',
      accessor: 'displayName',
      width: '10%',
      default: true,
      columnEditable: false,
      activeColumn: true,
      filter: {
        search: false,
        filterType: 'radio',
        typeFilter: true,
        filterList: [],
      },
    },
    {
      id: 3,
      header: 'EUA',
      accessor: 'euaRequired',
      width: '5%',
      default: true,
      columnEditable: true,
      activeColumn: true,
      capitalize: true,
    },
    {
      id: 4,
      header: 'Type',
      accessor: 'priceType',
      width: '5%',
      default: true,
      capitalize: true,
      columnEditable: true,
      activeColumn: true,
      Cell: (value) => (
        <div className="flex ">
          <Typography
            className=" ff-Roboto fs-12 capitalize"
            color={text}
          >
            {value?.priceType}/{value?.tradeType}
          </Typography>
        </div>
      ),
    },
    {
      id: 5,
      header: 'Timestamp',
      accessor: 'createdOn',
      width: '10%',
      default: true,
      columnEditable: true,
      activeColumn: true,
      Cell: (value) => (
        <div className="flex ">
          <Typography
            className="fw-regular ff-Roboto fs-12"
            color={text}
          >
            {moment(value?.createdOn).format('DD-MMM-YYYY HH:mm') || '-'}
          </Typography>
        </div>
      ),
    },
    {
      id: 6,
      header: 'Stage',
      accessor: 'stage',
      width: '10%',
      default: true,
      columnEditable: true,
      activeColumn: true,
      Cell: (value) => (
        <Stack
          direction={'row'}
          alignItems={'center'}
        >
          <Typography
            className="fw-medium ff-Roboto fs-12 capitalize"
            color={text}
          >
            {t(value?.stage) || '-'}
          </Typography>

          {value?.stage == 'request' && (
            <img
              className="ml-4"
              style={{ width: '14px', height: '14px' }}
              src={RequestLogo}
            />
          )}
          {value?.stage == 'advisory' && (
            <img
              className="ml-4"
              style={{ width: '13px', height: '13px' }}
              src={AdvisoryLogo}
            />
          )}
        </Stack>
      ),
    },
    {
      id: 7,
      header: 'Status',
      accessor: 'quoteStatus',
      width: '15%',
      default: true,
      columnEditable: true,
      activeColumn: true,
      jcc: true,
      Cell: (value) => (
        <div className="flex !justify-center">
          <Stack
            justifyContent={'center'}
            alignItems={'center'}
            className="fw-regular fs-12 ff-Roboto h-[28px] px-[10px] rounded-[16px] capitalize w-[120px]"
            style={{}}
            sx={{ backgroundColor: value?.quoteStatus ? screeningStatus[value?.quoteStatus] : 'transparent', color: value?.quoteStatus ? screeningStatus[`${value?.quoteStatus}Color`] : theme.palette.primary.text }}
          >
            {t(value?.quoteStatus) || '-'}
          </Stack>
        </div>
      ),
    },
  ];

  const [customerManagementColumn, setCustomerManagementColumn] = useState(columns);

  const [payload, setPayload] = useState({
    page: 1,
    limit: 10,
    filter: null,
    totalLength: 0,
    paginationPage: 0,
    role: null,
    field: '_id',
    sortOrder: 'desc',
  });

  const { state } = useLocation();
  const { companyDetails, user } = useSelector((state) => state.auth);

  let source = CancelToken.source();
  const text = theme.palette.primary.text;

  const handleChangePage = (event, newPage) => {
    setPayload((prevState) => ({ ...prevState, page: newPage + 1, paginationPage: newPage, filter: payload.filter }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPayload((prev) => ({ ...prev, limt: parseInt(event.target.value, 10), paginationPage: 1, page: 1, filter: payload.filter }));
  };

  //search filter
  const handleSearchEmit = (search) => {
    setPayload((prevState) => ({ ...prevState, paginationPage: 0, page: 1, filter: search }));
  };

  const handleToggleDrawer = (noti) => {
    if (noti) {
      setIsDrawerOpen(true);
    } else {
      setIsDrawerOpen(!isDrawerOpen);
    }
  };

  const selectRow = (sitem, sindex) => {
    const users = tradeList.map((item, index) => {
      item.Sno = payload.limit * (payload.page === 1 ? 0 : payload.page - 1) + index + 1;
      item.selected = item._id === sitem._id;
      return item;
    });
    setSelectedData(sitem);
    setTradeList(users);
    setIsDrawerOpen(true);
  };

  const handleGetQuoteList = async (fromSearch = true) => {
    setIsLoading(true);
    source.cancel();
    source = CancelToken.source();
    const response = await appapi.get(
      `/trader/quoteListForTrader/${user?.companyId}`,
      {
        pageNumber: payload.page,
        filter: payload.filter ? payload.filter : null,
        pageSize: payload.limit,
        role: payload.role,
        // field: payload.field === 'name' ? 'sortName' : payload.field === 'designation' ? 'sortDesignation' : payload.field,

        sortOrder: payload.sortOrder,
      },
      { cancelToken: source.token },
    );
    if (response.ok) {
      const data = response.data;
      if (data.status === '7400') {
        let userData = data?.response?.response;
        userData.map((item, index) => {
          item.Sno = payload.limit * (payload.page === 1 ? 0 : payload.page - 1) + index + 1;
          return item;
        });
        setTradeList(userData);
        setPayload((prev) => ({ ...prev, totalLength: data?.response?.length, paginationPage: payload.page === 1 ? 0 : payload.page - 1 }));
        setIsLoading(false);
      } else {
        setTradeList([]);
        setPayload((prev) => ({ ...prev, totalLength: 0, paginationPage: 0 }));
        setIsLoading(false);
      }
    } else {
      setTradeList([]);
      setPayload((prev) => ({ ...prev, totalLength: 0, paginationPage: 1 }));
      // setIsLoading(false);
    }
  };

  useEffect(() => {
    if(quotePermission.viewAccess || advisoriesPermission.viewAccess) {
      handleGetQuoteList();
    }
    return () => {
      if (source) {
        source.cancel();
      }
    };
    // eslint-disable-next-line
  }, [payload.page, payload.filter, payload.field, payload.sortOrder, payload.role, quotePermission.viewAccess , advisoriesPermission.viewAccess]);

  // useEffect(() => {
  //   if( state && state?.notificationData) {
  //     selectedData(state?.notificationData);
  //     handleToggleDrawer(true)
  //   }
  // }, [state?.notificationData])

  useEffect(() => {
    if (state && state?.notificationData) {
      setSelectedData(state?.notificationData);
      handleToggleDrawer(true);
    }
  }, [state?.notificationData]);

  const handleExcelDownload = async (format) => {
    const response = await appapi.get(
      `/trader/excelQuoteListForTrader/${user?.companyId}`,
      {
        filter: payload.filter ? payload.filter : null,
        role: payload.role,
        field: payload.field === 'name' ? 'sortName' : payload.field === 'designation' ? 'sortDesignation' : payload.field,
        sortOrder: payload.sortOrder,
        format,
        timeZone: moment.tz.guess(true),
      },
      { observe: 'response', responseType: 'blob' },
    );
    if (response.ok) {
      try {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        const ext = format === 'csv' ? 'csv' : 'xlsx';
        link.download = `TRADER${new Date().getTime()}.${ext}`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading:', error);
      }
    } else {
    }
  };

  return (
    (quotePermission.viewAccess || advisoriesPermission.viewAccess) ? <Stack
      flex={1}
      direction={'row'}
      className="m-[8px] rounded-[8px] bg-white"
    >
      <Stack flex={1}>
        <PageHeader
          handleFilterButton={true}
          handleOnSearchEmit={(search) => {
            handleSearchEmit(search);
          }}
          columnPreference={payload.totalLength > 0 ? true : false}
          columns={customerManagementColumn}
          applyColumnPreference={(c) => setCustomerManagementColumn(c)}
          showExport={payload.totalLength > 0 ? true : false}
          onExport={(type) => {
            handleExcelDownload(type);
          }}
          onRefresh={() => {
            if (payload.page === 1) {
              handleGetQuoteList();
            } else {
              setPayload((prevState) => ({ ...prevState, paginationPage: 0, page: 1 }));
            }
          }}
        >
          <Stack
            direction="row"
            flex={1}
          >
            {' '}
            <Stack flexGrow={1} />
            {advisoriesPermission?.addAccess && (
              <Button
                sx={{ color: theme.palette.primary.main }}
                className="fs-14 ff-Roboto fw-medium !mr-[20px]"
                onClick={() => {
                  setOpenRequestForm(true);
                }}
                variant="text"
              >
                {t('SEND ADVISORY')}
              </Button>
            )}
          </Stack>
        </PageHeader>
        <Stack className="relative overflow-auto flex-auto  h-0">
          <Datatable
            tableHeadBgColor={'#e2eeff'}
            columns={customerManagementColumn.filter((res) => res.activeColumn)}
            data={tradeList || []}
            isLoading={isLoading}
            onClickRow={(item, index) => {
              selectRow(item, index);
            }}
            onChangeFilter={(filterValues) => {
              const sortValues = filterValues?.sort;
              let columnUpdate = customerManagementColumn;
              columnUpdate[filterValues?.id].filter[`selectValue`] = filterValues?.filter.selectValue;
              if (sortValues) {
                columnUpdate = columnUpdate.map((res) => {
                  return {
                    ...res,
                    sortOrder: sortValues.field === res.accessor ? sortValues.order : null,
                  };
                });
              }
              setCustomerManagementColumn(columnUpdate);
              if (sortValues) {
                setPayload((prevState) => ({ ...prevState, limt: 10, paginationPage: 0, page: 1, field: sortValues.field, sortOrder: sortValues.order, role: filterValues?.filter.selectValue }));
              } else {
                setPayload((prevState) => ({ ...prevState, limt: 10, paginationPage: 0, page: 1, role: filterValues?.filter.selectValue }));
              }
            }}
            sortChange={(sortValues) => {
              const columnUpdate = customerManagementColumn.map((res) => {
                return {
                  ...res,
                  sortOrder: sortValues.field === res.accessor ? sortValues.order : null,
                };
              });
              setCustomerManagementColumn([...columnUpdate]);
              setPayload((prevState) => ({ ...prevState, limt: 10, paginationPage: 0, page: 1, field: sortValues.field, sortOrder: sortValues.order }));
            }}
          />
        </Stack>
        {
          <Stack
            sx={{ width: '100%' }}
            className="relative pl-[16px]   inputFormStyle"
          >
            <Box className="flex  justify-end items-center h-[56px] w-[100%] p-[5px]">
              <TablePagination
                sx={{ borderBottom: 0 }}
                rowsPerPageOptions={[]}
                count={payload.totalLength ? payload.totalLength : 0}
                rowsPerPage={payload.limit ? payload.limit : 10}
                page={payload.paginationPage ? payload.paginationPage : 0}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </Box>
          </Stack>
        }
      </Stack>
      <Stack>
        {isDrawerOpen && (
          <TradeDetails
            trade={selectedData}
            isDrawerOpen={isDrawerOpen}
            init={() => {
              handleGetQuoteList();
            }}
            toggleDrawer={() => {
              if (selectedData) {
                handleToggleDrawer();
              }
            }}
            openEditUser={() => {
              // setAddEditUserOpen(true);
              // setType('edit');
            }}
            // init={() => handleGetUser()}
          />
        )}
      </Stack>
      {openRequestForm && (
        <SendAdvisory
          modelOpen={openRequestForm}
          onModelHandleClose={(success) => {
            if (success) {
              handleGetQuoteList();
            }
            setOpenRequestForm(false);
          }}
        />
      )}
    </Stack>: <AccessDenied />
  );
}
